import { cn } from '@/common/utilities.ts'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import StackedTable from '@/common/components/StackedTable.tsx'
import { Input } from '@/app/common/components/input/Input.tsx'
import { ChangeEvent, SyntheticEvent, useEffect, useRef, useState } from 'react'
import { Button } from '@/app/common/components/button/Button.tsx'
import { Form } from '@/common/components/Form.tsx'
import { useSelector } from 'react-redux'
import { RootState } from '@/storage/store.ts'
import { RoleQueries } from '@/queries/roles/queries.ts'
import { useQuery } from '@tanstack/react-query'
import { UpdateRolePermissions } from '@/queries/roles/roles.ts'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { EllipsesLoader } from '@/common/components/EllipsesLoader.tsx'
import { CircleLoader } from '@/common/components/CircleLoader.tsx'
import { PlusIcon } from '@/common/icons/arrows.tsx'
import { EditIconGeneral, TrashCanIcon } from '@/common/icons/general.tsx'
import EditRoleView from './EditRoleView.tsx'
import { Role } from '@/common/types_and_interfaces.ts'
import { UpdateRolePermissionObj } from '@/queries/roles/schemas.ts'
import NewRoleView from '@/pages/setup/accesses/NewRoleView.tsx'
import { useHasPrivilege } from '../../../../hook/useHasPrivilege.tsx'
import usePermissionAlert from '../../../../hook/usePermissionAlert.tsx'
import { deleteRole } from '@/queries/users/requests.ts'
import { useUserPrivileges } from '../../../../hook/useUserPrivileges.tsx'
import { LogUserActivity } from '@/apis/users/requests.ts'
import { LOG_TYPES } from '@/common/constants.ts'

type RolePermissions = {
  resource_id: number
  resource_name: string
  permissions: Permissions
}

type Permissions = {
  create: number
  read: number
  edit: number
  delete: number
}

const ONE_MINUTE = 60 * 1000

export default function AccessView() {
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [editRole, setEditRole] = useState<Role | null>(null)
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const [Roles, setRoles] = useState<Role[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [newRoleModal, setNewRoleModal] = useState(false)
  const [roleAccesses, setRoleAccesses] = useState<RolePermissions[]>([])
  const omitRoles = ['admin']

  // Resource IDs to hide from the role-permissions UI.
  // These represent system/global modules managed elsewhere, so their
  // permissions are not user-configurable here.
  const omitResources = [4, 3, 17, 18, 19, 20, 21, 22]

  const canCreateRoles = useHasPrivilege('roles_create')
  const canRolesEdit = useHasPrivilege('roles_edit')
  const privileges = useUserPrivileges()
  const { showPermissionDeniedMessage } = usePermissionAlert()

  const roleQueryResult = useQuery(
    RoleQueries.index(selectedProgramYear?.id ?? 0, {
      enabled: !!selectedProgramYear,
    })
  )

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Roles',
      }) // Log page view
      hasLoggedPageView.current = true // Mark as executed
    }
  }, [])

  useEffect(() => {
    setRoles(
      (roleQueryResult.data?.data ?? []).filter(
        (r) => !omitRoles.includes(r.code)
      )
    )
  }, [roleQueryResult?.data])

  const RolePermissionsData = useQuery(
    RoleQueries.getRolePermissionsByRoleId(selectedRole?.id ?? 0, {
      enabled: false,
      staleTime: ONE_MINUTE,
      refetchOnMount: true,
      select: (data) => {
        return data.filter(
          (f: RolePermissions) => !omitResources.includes(f.resource_id)
        )
      },
    })
  )

  useEffect(() => {
    if (RolePermissionsData?.data) {
      setRoleAccesses(RolePermissionsData.data as RolePermissions[])
    }
  }, [RolePermissionsData?.data])

  const { fireAlert } = useAppAlert()

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()
    setIsLoading(true)
    const req: Array<UpdateRolePermissionObj> = []
    roleAccesses.map((resource) =>
      req.push({
        resource_id: resource.resource_id,
        create: resource.permissions.create == 1,
        delete: resource.permissions.delete == 1,
        edit: resource.permissions.edit == 1,
        read: resource.permissions.read == 1,
      })
    )
    const { error, data } = await UpdateRolePermissions(
      { permissions: req },
      selectedRole!.id
    )
    setIsLoading(false)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
      return
    } else {
      roleQueryResult.refetch()
      await fireAlert({
        variant: 'success',
        message: data.message,
      })
    }
  }

  const refreshPage = () => {
    roleQueryResult.refetch()
  }

  const onDeleteRole = async (roleId: number) => {
    fireAlert({
      variant: 'warning',
      confirmBtnText: 'Yes',
      denyBtnText: 'No',
      denyBtn: true,
      message: 'Are you sure you want to Delete?',
    }).then(async ({ confirmed }) => {
      if (confirmed) {
        const response = await deleteRole(roleId)

        if (response) {
          setRoles((prevRoles) =>
            prevRoles.filter((role) => role.id !== roleId)
          )
        }
      }
    })
  }

  // Helper function to update permission using event and permission type
  const updatePermission = (
    index: number,
    field: keyof Permissions,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setRoleAccesses((pre) =>
      pre.map((access, i) =>
        i === index
          ? {
              ...access,
              permissions: {
                ...access.permissions,
                [field]: event.target.checked ? 1 : 0,
              },
            }
          : access
      )
    )
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-7.5 mt-5">
        <div className="col-span-1 w-full">
          <Card className="w-full shadow-card">
            <CardHeader className="flex justify-between items-center text-md font-semibold py-5">
              <h3 className="text-md font-bold text-gray-900 flex">Roles</h3>
              <Button
                size="sm"
                className="font-bold justify-end"
                onClick={() => {
                  if (canCreateRoles) {
                    setNewRoleModal(true)
                  } else {
                    showPermissionDeniedMessage()
                  }
                }}
              >
                <PlusIcon />
              </Button>
            </CardHeader>
            <CardContent className="p-0 lg:p-0">
              <StackedTable.Table>
                <StackedTable.Body>
                  {Roles.map((role, index) => (
                    <tr key={index}>
                      <td
                        className={cn(
                          'relative w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium sm:w-auto sm:max-w-none sm:pl-6',
                          selectedRole && selectedRole.code === role.code
                            ? 'text-primary'
                            : 'text-gray-900',
                          {
                            'border-t border-transparent': index !== 0,
                            'cursor-pointer': canRolesEdit,
                            'cursor-not-allowed opacity-50': !canRolesEdit,
                          }
                        )}
                        onClick={(e) => {
                          if (canRolesEdit) {
                            setSelectedRole(role)
                            setTimeout(() => {
                              RolePermissionsData.refetch()
                            }, 100)
                          } else {
                            e.preventDefault()
                            e.stopPropagation()
                            showPermissionDeniedMessage()
                          }
                        }}
                      >
                        <div className="flex justify-between">
                          <div className="flex items-center">
                            {role.code !== 'admin' && role.name}
                          </div>
                          {index !== 0 ? (
                            <div className="absolute -top-px left-0 right-0 h-px bg-gray-200" />
                          ) : null}
                          <div className="flex">
                            <div
                              onClick={(e) => {
                                if (canRolesEdit) {
                                  setEditRole(role)
                                } else {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  showPermissionDeniedMessage()
                                }
                              }}
                              className={cn(
                                'group',
                                !canRolesEdit && 'cursor-not-allowed opacity-50'
                              )}
                            >
                              <EditIconGeneral className="text-lg text-gray-400 group-hover:text-primary" />
                            </div>

                            {index > 5 && (
                              <div
                                onClick={(e) => {
                                  if (privileges.user.roles_delete) {
                                    onDeleteRole(role?.id)
                                  } else {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    showPermissionDeniedMessage()
                                  }
                                }}
                                className={cn(
                                  'group',
                                  !canRolesEdit &&
                                    'cursor-not-allowed opacity-50'
                                )}
                              >
                                <TrashCanIcon className="text-lg text-gray-400 group-hover:text-primary ml-2" />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {selectedRole?.id === 0 && (
                    <tr>
                      <td
                        className={cn(
                          'relative w-full max-w-0 py-2 pr-5 text-sm font-medium sm:w-auto sm:max-w-none sm:pl-6 cursor-pointer',
                          //selectedRole && selectedRole.code == role.code ? 'text-primary' : 'text-gray-900',
                          'border-t border-transparent'
                        )}
                      >
                        <Input
                          id={`create-rol`}
                          defaultValue={selectedRole?.code ?? 'New Rol'}
                          className="shadow-none pl-1"
                          type="text"
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setSelectedRole({
                              id: 0,
                              program_id: 0,
                              name: e.target.value ?? 'new Role',
                              code: e.target.value ?? 'new Role',
                              description: e.target.value ?? 'new Role',
                            })
                          }
                        />

                        <div className="absolute -top-px left-0 right-0 h-px bg-gray-200" />
                      </td>
                    </tr>
                  )}
                </StackedTable.Body>
              </StackedTable.Table>
            </CardContent>
          </Card>
        </div>
        <div className="col-span-1 md:col-span-2 lg:col-span-3">
          <div className="mx-auto p-0">
            <Form onSubmit={handleSubmit}>
              {selectedRole && (
                <Card className="w-full shadow-card">
                  <CardHeader className="flex justify-between items-center text-md font-semibold py-5">
                    <div className="text-left text-md font-bold text-gray-900">
                      {selectedRole.name} Role Permissions
                    </div>
                    {RolePermissionsData.isLoading ? (
                      <CircleLoader size="sm" />
                    ) : (
                      ''
                    )}
                  </CardHeader>
                  <CardContent className="p-0 lg:p-0">
                    <StackedTable.Table>
                      <StackedTable.Header>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 "
                        >
                          Module
                        </th>
                        <th
                          scope="col"
                          className="px-1 xl:px-3 text-center text-sm font-semibold text-gray-900"
                        >
                          Create
                        </th>
                        <th
                          scope="col"
                          className="px-1 xl:px-3 text-center text-sm font-semibold text-gray-900"
                        >
                          Read
                        </th>
                        <th
                          scope="col"
                          className="px-1 xl:px-3 text-center text-sm font-semibold text-gray-900"
                        >
                          Update
                        </th>
                        <th
                          scope="col"
                          className="px-1 xl:px-3 text-center text-sm font-semibold text-gray-900"
                        >
                          Delete
                        </th>
                      </StackedTable.Header>
                      <StackedTable.Body>
                        {roleAccesses.map((module, index) => (
                          <tr key={index}>
                            <td
                              className={cn(
                                'relative w-full min-w-[100px] max-w-0 py-4 pl-4 xl:pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6',
                                {
                                  'border-t border-transparent': index !== 0,
                                }
                              )}
                            >
                              {' '}
                              {module.resource_name}
                              {index !== 0 ? (
                                <div className="absolute -top-px left-0 right-0 h-px bg-gray-200" />
                              ) : null}
                            </td>
                            <td
                              className={cn(
                                'px-1 xl:px-3 py-3.5 text-sm text-gray-500 text-center',
                                {
                                  'border-t border-gray-200': index !== 0,
                                }
                              )}
                            >
                              <Input
                                id={`create-${module.resource_id}`}
                                type="checkbox"
                                checked={module.permissions.create == 1}
                                onChange={(e) =>
                                  updatePermission(index, 'create', e)
                                }
                              />
                            </td>
                            <td
                              className={cn(
                                'px-1 xl:px-3 py-3.5 text-sm text-gray-500 text-center',
                                {
                                  'border-t border-gray-200': index !== 0,
                                }
                              )}
                            >
                              <Input
                                id={`read-${module.resource_id}`}
                                type="checkbox"
                                checked={module.permissions.read == 1}
                                onChange={(e) =>
                                  updatePermission(index, 'read', e)
                                }
                              />
                            </td>
                            <td
                              className={cn(
                                'px-1 xl:px-3 py-3.5 text-sm text-gray-500 text-center',
                                {
                                  'border-t border-gray-200': index !== 0,
                                }
                              )}
                            >
                              <Input
                                id={`update-${module.resource_id}`}
                                type="checkbox"
                                checked={module?.permissions?.edit == 1}
                                onChange={(e) =>
                                  updatePermission(index, 'edit', e)
                                }
                              />
                            </td>
                            <td
                              className={cn(
                                'relative px-1 xl:px-3 py-3.5 text-sm text-gray-500 text-center',
                                {
                                  'border-t border-transparent': index !== 0,
                                }
                              )}
                            >
                              <Input
                                id={`delete-${module.resource_id}`}
                                type="checkbox"
                                checked={module?.permissions?.delete == 1}
                                onChange={(e) =>
                                  updatePermission(index, 'delete', e)
                                }
                              />
                              {index !== 0 ? (
                                <div className="absolute -top-px left-0 right-0 h-px bg-gray-200" />
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </StackedTable.Body>
                    </StackedTable.Table>
                  </CardContent>
                  <CardFooter className="py-0">
                    <div className="my-5 flex items-center justify-end gap-x-2">
                      {isLoading ? (
                        <EllipsesLoader size="md" />
                      ) : (
                        <>
                          <Button>
                            {selectedRole.id == 0
                              ? 'Create New Rol'
                              : 'Save Changes'}
                          </Button>
                        </>
                      )}
                    </div>
                  </CardFooter>
                </Card>
              )}
            </Form>
          </div>
        </div>
      </div>
      <EditRoleView
        role={editRole}
        refreshPage={refreshPage}
        close={() => {
          setEditRole(null)
        }}
      />
      <NewRoleView
        open={newRoleModal}
        refreshPage={refreshPage}
        close={() => {
          setNewRoleModal(false)
        }}
      />
    </div>
  )
}
